import React from 'react';
import { hasEnoughPoints, INSUFFICIENT_POINTS_MESSAGE, QUOTA_OPERATION } from './aiQuota';

const TOP_N_OPTIONS = [5, 10, 20, 50];
const SALARY_OPTIONS = Array.from({ length: 15 }, (_, i) => (i + 1) * 100); // 100万円刻み

// DP-M-002: 残量に応じた警告レベル (Success/Warning/Error)
function quotaLevel(remainingPoints, maxPoints) {
    if (remainingPoints <= 0) return 'error';
    if (maxPoints > 0 && remainingPoints / maxPoints <= 0.1) return 'warning';
    return 'success';
}

/**
 * AI マッチング抽出条件フォーム (SCR-M-001、IN-M-003〜005、DP-M-012、BTN-M-002)
 *
 * 業界フィルタ (IN-M-002) は廃止した。対象の求人は一覧 (JobSelectionList) で選ぶ (§4.6、Issue #779)。
 *
 * @param {{
 *   selectedCount: number,
 *   showNoSelectionError: boolean,
 * }} props - selectedCount: 選択した求人の件数 (DP-M-012)。
 *   showNoSelectionError: §5.2 No.4 の文言を出すか (求人プールが 0 件のときは No.3 を出すため呼び出し側で false にする)
 */
function MatchingFilterForm({
    filters,
    onChange,
    onSubmit,
    submitting,
    submitDisabled,
    quota,
    quotaLoading,
    selectedCount,
    showNoSelectionError,
}) {
    const salaryError =
        filters.salaryMin !== '' &&
        filters.salaryMax !== '' &&
        Number(filters.salaryMin) > Number(filters.salaryMax);

    // BTN-M-002 ②: 残量がマッチングの実行に必要なポイント (required_points.MATCHING_SCORING) 未満の
    // ときは押せない (§5.2 No.1・§6.4.4、Issue #882。改める前は「残量が 0 より大きい」で判定していた)。
    // 前回と同じ選択・条件の再実行 (サーバが前回の結果を再利用しポイントを使わない) も押せなくする
    // (画面は再利用されるかを判定できないため。PM 判断 2026-09-18)
    const insufficientPoints = !hasEnoughPoints(quota, QUOTA_OPERATION.matchingScoring);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (salaryError || submitDisabled || insufficientPoints) return;
        onSubmit();
    };

    return (
        <form className="matching-filter-form" onSubmit={handleSubmit}>
            <div className="matching-filter-row">
                <div className="matching-filter-group">
                    <label className="detail-label" htmlFor="matching-salary-min">年収下限</label>
                    <select
                        id="matching-salary-min"
                        className="matching-select"
                        value={filters.salaryMin}
                        onChange={(e) => onChange({ salaryMin: e.target.value })}
                    >
                        <option value="">指定なし</option>
                        {SALARY_OPTIONS.map((v) => (
                            <option key={v} value={v}>{v}万円</option>
                        ))}
                    </select>
                </div>

                <div className="matching-filter-group">
                    <label className="detail-label" htmlFor="matching-salary-max">年収上限</label>
                    <select
                        id="matching-salary-max"
                        className="matching-select"
                        value={filters.salaryMax}
                        onChange={(e) => onChange({ salaryMax: e.target.value })}
                    >
                        <option value="">指定なし</option>
                        {SALARY_OPTIONS.map((v) => (
                            <option key={v} value={v}>{v}万円</option>
                        ))}
                    </select>
                </div>

                <div className="matching-filter-group">
                    <label className="detail-label" htmlFor="matching-top-n">上位N件</label>
                    <select
                        id="matching-top-n"
                        className="matching-select"
                        value={filters.topN}
                        onChange={(e) => onChange({ topN: Number(e.target.value) })}
                    >
                        {TOP_N_OPTIONS.map((v) => (
                            <option key={v} value={v}>{v}件</option>
                        ))}
                    </select>
                </div>
            </div>

            {salaryError && (
                <p className="modal-error-message" role="alert">
                    年収下限は年収上限以下で指定してください
                </p>
            )}

            <div className="matching-filter-actions">
                {!quotaLoading && quota && (
                    <p className={`matching-quota-badge matching-quota-badge--${quotaLevel(quota.remaining_points, quota.max_points)}`}>
                        残 {quota.remaining_points} ポイント（{quota.used_points}/{quota.max_points}pt 使用済み）
                    </p>
                )}
                {/* DP-M-012: 対象の説明 (BTN-M-002 の直上。§4.6.1 論点 1・8) */}
                <div className="matching-selection-summary">
                    <p className="matching-selection-summary-target">対象: 選択した {selectedCount} 件の求人</p>
                    <p className="matching-selection-summary-note">
                        選択した求人のうち、年収の条件に合うものから上位 {filters.topN} 件まで推薦します
                    </p>
                </div>
                <button
                    type="submit"
                    className="matching-execute-button"
                    disabled={submitting || salaryError || submitDisabled || insufficientPoints}
                    aria-label={`AIマッチングを実行${quota ? `（残${quota.remaining_points}ポイント）` : ''}`}
                >
                    {submitting
                        ? '実行中...'
                        : quota
                            ? `✨ AIマッチングを実行（残 ${quota.remaining_points} ポイント）`
                            : '✨ AIマッチングを実行'}
                </button>
                {showNoSelectionError && (
                    <p className="modal-error-message" role="alert">
                        求人を 1 件以上選択してください。
                    </p>
                )}
                {/* §5.2 No.1: 文言にポイントの数は入れない (Issue #882) */}
                {insufficientPoints && (
                    <p className="modal-error-message" role="alert">
                        {INSUFFICIENT_POINTS_MESSAGE}
                    </p>
                )}
            </div>
        </form>
    );
}

export default MatchingFilterForm;
